import { ReactNode } from 'react';
import { FormInstance } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import type { Appointment } from '@/api/mcp-appointments';

/**
 * Render slots available in the CreateAppointmentDialog for module extensions
 */
export enum AppointmentDialogSlot {
  /** Before resource selector - good for displaying module context */
  BEFORE_RESOURCE = 'before-resource',
  /** After duration input - good for module-specific configuration */
  AFTER_DURATION = 'after-duration',
  /** After appointment type selector - good for module-specific appointment types */
  AFTER_APPOINTMENT_TYPE = 'after-appointment-type',
  /** Before notes field - good for module-specific metadata fields */
  BEFORE_NOTES = 'before-notes',
  /** After notes field - good for additional module fields */
  AFTER_NOTES = 'after-notes'
}

/**
 * Context provided to module extension components
 */
export interface AppointmentExtensionContext {
  /** Form instance for accessing and updating form values */
  form: FormInstance;
  /** Current form values */
  formValues: Record<string, any>;
  /** Module-specific data passed from the module */
  moduleData?: any;
  /** Whether the form is being submitted */
  isSubmitting: boolean;
}

/**
 * Validation result from module extension
 */
export interface AppointmentExtensionValidation {
  /** Whether validation passed */
  valid: boolean;
  /** Error message if validation failed */
  error?: string;
  /** Field-level errors */
  fieldErrors?: Record<string, string>;
}

/**
 * Context builder result from module extension
 * Module-specific context data that will be stored in the appointment.context field
 * 
 * The final stored format will be: { source: string, data: { ... } }
 * If source is included in the returned data, it will be extracted and used as the top-level source.
 * All other fields will be wrapped in the data field.
 */
export interface AppointmentContextData {
  /** Module identifier (optional - will use moduleId if not provided) */
  source?: string;
  /** Module-specific context data */
  [key: string]: any;
}

/**
 * @deprecated Use AppointmentContextData instead. This interface is kept for backward compatibility.
 * Metadata builder result from module extension
 */
export interface AppointmentExtensionMetadata {
  /** Module identifier */
  source: string;
  /** Module-specific metadata */
  [key: string]: any;
}

/**
 * Interception result for appointment creation
 * 
 * Modules can return this from onBeforeAppointmentCreated to optionally skip creation.
 * When skipped, modules can handle the data however they want (e.g., via webhook, external API, etc.)
 */
export interface AppointmentCreationInterception {
  /** If true, skip the actual appointment creation and treat as success */
  skip?: boolean;
  /** Custom result to pass to onSuccess callback when skipping */
  result?: any;
}

/**
 * Slot component props
 */
export interface AppointmentSlotComponentProps {
  context: AppointmentExtensionContext;
}

/**
 * Module appointment extension interface
 * 
 * Modules can implement this interface to extend the CreateAppointmentDialog
 * with module-specific functionality, fields, and metadata.
 * 
 * @example
 * ```typescript
 * const jobModuleExtension: ModuleAppointmentExtension = {
 *   moduleId: 'job-module',
 *   getSlotComponents: (slot) => {
 *     if (slot === AppointmentDialogSlot.BEFORE_RESOURCE) {
 *       return <JobApplicationInfo application={moduleData.application} />;
 *     }
 *     if (slot === AppointmentDialogSlot.AFTER_APPOINTMENT_TYPE) {
 *       return <AppointmentFormatSelector />;
 *     }
 *     return null;
 *   },
 *   buildContext: (formValues, moduleData) => ({
 *     source: 'job-module',
 *     applicationId: moduleData.application.id,
 *     position: moduleData.application.position
 *   }),
 *   validate: () => ({ valid: true })
 * };
 * ```
 *
 * Meeting format and join URLs are native appointment fields (`format`, `meetingUrl`,
 * `meetingModeratorUrl`) owned by the shared create dialog — do not put them in context.
 */
export interface ModuleAppointmentExtension {
  /** Module identifier (must match ModuleDefinition.id) */
  moduleId: string;
  
  /**
   * Get components to render in specific slots
   * @param slot The slot to render components for
   * @param context Context with form instance and values
   * @returns React node to render, or null/undefined to render nothing
   */
  getSlotComponents?: (
    slot: AppointmentDialogSlot,
    context: AppointmentExtensionContext
  ) => ReactNode | null;
  
  /**
   * Build module-specific context from form values
   * @param formValues Current form values
   * @param moduleData Module-specific data passed to the dialog
   * @returns Context object to attach to the appointment.context field
   */
  buildContext?: (
    formValues: Record<string, any>,
    moduleData?: any
  ) => AppointmentContextData | null;

  /**
   * @deprecated Use buildContext instead. This method is kept for backward compatibility.
   * Build module-specific metadata from form values
   * @param formValues Current form values
   * @param moduleData Module-specific data passed to the dialog
   * @returns Metadata object to attach to the appointment
   */
  buildMetadata?: (
    formValues: Record<string, any>,
    moduleData?: any
  ) => AppointmentExtensionMetadata | null;
  
  /**
   * Validate module-specific form values
   * @param formValues Current form values
   * @param moduleData Module-specific data passed to the dialog
   * @returns Validation result
   */
  validate?: (
    formValues: Record<string, any>,
    moduleData?: any
  ) => AppointmentExtensionValidation | Promise<AppointmentExtensionValidation>;
  
  /**
   * Get initial form values based on module data
   * @param moduleData Module-specific data passed to the dialog
   * @returns Initial form values to pre-populate
   */
  getInitialValues?: (moduleData?: any) => Record<string, any>;
  
  /**
   * Called before appointment creation to allow modules to optionally skip creation
   * 
   * Modules can use this hook to:
   * - Skip creation and handle it externally (e.g., via webhook, external API)
   * - Perform side effects before creation
   * 
   * If a module wants to prevent creation due to validation errors, it should throw an error
   * instead of returning a block result. This will be caught and shown to the user.
   * 
   * @param formValues Form values that would be used to create the appointment
   * @param appointmentData Prepared appointment data
   * @param moduleData Module-specific data
   * @returns Interception result or boolean (for backward compatibility - true = skip, false/undefined = proceed)
   */
  onBeforeAppointmentCreated?: (
    formValues: Record<string, any>,
    appointmentData: any,
    moduleData?: any
  ) => 
    | boolean 
    | Promise<boolean> 
    | AppointmentCreationInterception 
    | Promise<AppointmentCreationInterception> 
    | void 
    | Promise<void>;
  
  /**
   * Called when appointment is successfully created
   * @param appointment Created appointment
   * @param formValues Form values used to create the appointment
   * @param moduleData Module-specific data
   */
  onAppointmentCreated?: (
    appointment: any,
    formValues: Record<string, any>,
    moduleData?: any
  ) => void | Promise<void>;
  
  /**
   * Called when appointment creation fails
   * @param error Error that occurred
   * @param formValues Form values that were attempted
   * @param moduleData Module-specific data
   */
  onAppointmentError?: (
    error: Error,
    formValues: Record<string, any>,
    moduleData?: any
  ) => void | Promise<void>;
  
  /**
   * Called when appointment dialog is cancelled
   * @param formValues Current form values
   * @param moduleData Module-specific data
   */
  onAppointmentCancelled?: (
    formValues: Record<string, any>,
    moduleData?: any
  ) => void;
}

/**
 * Detail section for appointment details popup
 */
export interface AppointmentDetailSection {
  /** Unique identifier for the section */
  id: string;
  /** Section title (optional) */
  title?: string;
  /** Section content */
  content: ReactNode;
  /** Position in the details popup */
  position: 'before' | 'after' | 'custom';
  /** Order for sorting sections (lower numbers appear first) */
  order?: number;
}

/**
 * Custom field for appointment details Descriptions component
 */
export interface AppointmentCustomField {
  /** Field label */
  label: string;
  /** Field value (can be ReactNode for custom rendering) */
  value: ReactNode;
  /** Column span (1-4, default: 1) */
  span?: number;
}

/**
 * Action button for appointment details popup
 */
export interface AppointmentDetailAction {
  /** Unique key for the action */
  key: string;
  /** Button label */
  label: string;
  /** Optional icon */
  icon?: ReactNode;
  /** Click handler */
  onClick: (appointment: Appointment) => void | Promise<void>;
  /** Button variant */
  variant?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  /** Whether button is danger style */
  danger?: boolean;
  /** Position relative to default actions */
  position?: 'before' | 'after';
  /** Order for sorting actions (lower numbers appear first) */
  order?: number;
  /** Whether action is disabled */
  disabled?: boolean;
}

/**
 * Status display configuration
 */
export interface AppointmentStatusDisplay {
  /** Status text */
  text: string;
  /** Status color (Ant Design Tag color) */
  color: string;
  /** Optional icon */
  icon?: ReactNode;
  /** Optional badge */
  badge?: ReactNode;
}

/**
 * Appointment display extension interface
 * 
 * Modules can implement this interface to customize how appointments are displayed
 * in calendars, details popups, and table views.
 */
export interface AppointmentDisplayExtension {
  /** Module identifier (must match ModuleDefinition.id) */
  moduleId: string;

  /**
   * Get custom CSS styles for calendar event
   * @param appointment The appointment being rendered
   * @param contextData Parsed context data from appointment.context.data
   * @returns CSS properties to apply to the calendar event
   */
  getEventStyle?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => React.CSSProperties | undefined;

  /**
   * Get custom icon for calendar event
   * @param appointment The appointment being rendered
   * @param contextData Parsed context data from appointment.context.data
   * @returns React node for the icon, or undefined to use default
   */
  getEventIcon?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => ReactNode | undefined;

  /**
   * Get custom label/text for calendar event
   * @param appointment The appointment being rendered
   * @param contextData Parsed context data from appointment.context.data
   * @returns Custom label string, or undefined to use default (clientName)
   */
  getEventLabel?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => string | undefined;

  /**
   * Get custom tooltip content for calendar event
   * @param appointment The appointment being rendered
   * @param contextData Parsed context data from appointment.context.data
   * @returns React node for tooltip content, or undefined to use default
   */
  getEventTooltip?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => ReactNode | undefined;

  /**
   * Custom render function for calendar event content
   * @param appointment The appointment being rendered
   * @param contextData Parsed context data from appointment.context.data
   * @param defaultRender Function to render default content
   * @returns React node for event content, or undefined to use default
   */
  renderEventContent?: (
    appointment: Appointment,
    contextData: Record<string, any>,
    defaultRender: () => ReactNode
  ) => ReactNode | undefined;

  /**
   * Get custom sections to add to appointment details popup
   * @param appointment The appointment being displayed
   * @param contextData Parsed context data from appointment.context.data
   * @returns Array of detail sections
   */
  getDetailSections?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => AppointmentDetailSection[];

  /**
   * Get custom fields to add to appointment details Descriptions
   * @param appointment The appointment being displayed
   * @param contextData Parsed context data from appointment.context.data
   * @returns Array of custom fields
   */
  getCustomFields?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => AppointmentCustomField[];

  /**
   * Get custom action buttons for appointment details popup
   * @param appointment The appointment being displayed
   * @param contextData Parsed context data from appointment.context.data
   * @returns Array of action buttons
   */
  getDetailActions?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => AppointmentDetailAction[];

  /**
   * Get custom table columns for appointment tables
   * @param appointments Array of appointments (for context)
   * @returns Array of Ant Design table columns
   */
  getTableColumns?: (
    appointments: Appointment[]
  ) => ColumnsType<Appointment> | undefined;

  /**
   * Get custom CSS class name for table row
   * @param appointment The appointment row
   * @param contextData Parsed context data from appointment.context.data
   * @returns CSS class name(s), or undefined
   */
  getRowClassName?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => string | undefined;

  /**
   * Get custom status display
   * @param appointment The appointment
   * @param contextData Parsed context data from appointment.context.data
   * @returns Status display configuration, or undefined to use default
   */
  getStatusDisplay?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => AppointmentStatusDisplay | undefined;

  /**
   * Get priority indicator badge
   * @param appointment The appointment
   * @param contextData Parsed context data from appointment.context.data
   * @returns React node for priority indicator, or undefined
   */
  getPriorityIndicator?: (
    appointment: Appointment,
    contextData: Record<string, any>
  ) => ReactNode | undefined;
}

/**
 * Extended module appointment extension that includes both creation and display extensions
 */
export interface ExtendedModuleAppointmentExtension extends ModuleAppointmentExtension, AppointmentDisplayExtension {}
